import React, { useCallback, useEffect, useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import Dialog from '../../../common/Dialog';
import Button from '../../../common/Button';
import Loading from '../../../common/Loading';
import StatusChip from '../../../common/StatusChip';
import { applicantService } from '../../../../services/applicant.service';
import { formatApiLocalDateTime } from '../../../../utils/date';
import type {
  ProcessHistoryResponse,
  ProcessStage,
  StageStatus,
  PendingStep,
} from '../../../../services/types/processStatus.types';

interface ProcessHistoryDialogProps {
  open: boolean;
  applicantId: number;
  onClose: () => void;
  /** Opens the Additional Info page for the applicant (questions, answers and stage actions live there). */
  onViewDetails?: () => void;
}

// Same common wording as the grid status.
const STAGE_STATUS_LABELS: Record<StageStatus, string> = {
  complete: 'Complete',
  in_progress: 'In Progress',
  not_started: 'Not Started',
  not_applicable: 'N/A',
};

const STAGE_STATUS_CHIP: Record<StageStatus, { text: string; bg: string }> = {
  complete: { text: '#528900', bg: '#5289001A' },
  in_progress: { text: '#B8860B', bg: '#FBB32C1A' },
  not_started: { text: '#667085', bg: '#F1F3FF' },
  not_applicable: { text: '#667085', bg: '#EAECF0' },
};

const PENDING_STEP_LABELS: Record<PendingStep, string> = {
  WELCOME: 'the welcome message',
  VACANCY_CODE: 'the vacancy code',
  CV_COMPARISON: 'the CV comparison',
  GENERAL_QUESTIONS: 'the general questions',
};

const isMedium = (medium: string | undefined, expected: string): boolean =>
  (medium ?? '').trim().toLowerCase() === expected;

const ProcessHistoryDialog: React.FC<ProcessHistoryDialogProps> = ({
  open,
  applicantId,
  onClose,
  onViewDetails,
}) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<ProcessHistoryResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!applicantId) return;

    setLoading(true);
    setLoadError(null);
    try {
      const response = await applicantService.getProcessHistory(applicantId);
      setHistory(response.data);
    } catch (error: unknown) {
      console.error('Error fetching process history:', error);
      setHistory(null);
      const message =
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
          ? (error as { message: string }).message
          : 'Failed to load process history. Please try again.';
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, [applicantId]);

  useEffect(() => {
    if (open && applicantId) {
      setHistory(null);
      setLoadError(null);
      fetchHistory();
    }
  }, [open, applicantId, fetchHistory]);

  const getStageIcon = (stage: ProcessStage) => {
    const iconSx = { fontSize: 24 };
    switch (stage.icon) {
      case 'check_circle':
        return <CheckCircleIcon sx={{ ...iconSx, color: '#528900' }} />;
      case 'hourglass_empty':
        return <HourglassEmptyIcon sx={{ ...iconSx, color: '#E7B317' }} />;
      case 'radio_button_unchecked':
      default:
        return <RadioButtonUncheckedIcon sx={{ ...iconSx, color: '#9C9C9C' }} />;
    }
  };

  /** One summary row: name, status, progress and times. No questions or answers. */
  const renderStage = (stage: ProcessStage) => {
    const colors = STAGE_STATUS_CHIP[stage.status];

    return (
      <Box
        key={stage.id}
        data-testid={`process-stage-${stage.id}`}
        sx={{
          border: '1px solid #EAECF0',
          borderRadius: '8px',
          padding: '14px 16px',
          mb: 2,
          backgroundColor: '#FFFFFF',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box sx={{ pt: 0.25, flexShrink: 0 }}>{getStageIcon(stage)}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
            <Typography
              sx={{ fontSize: '15px', fontWeight: 600, color: '#101828', wordBreak: 'break-word' }}
            >
              {stage.name}
            </Typography>
            <StatusChip
              label={STAGE_STATUS_LABELS[stage.status]}
              sx={{
                color: colors.text,
                backgroundColor: colors.bg,
                fontSize: '12px',
                fontWeight: 500,
                height: '20px',
                '& .MuiChip-label': {
                  color: colors.text,
                  paddingLeft: '8px',
                  paddingRight: '8px',
                },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {stage.jiraStatusName && (
              <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 400 }}>
                Jira status: {stage.jiraStatusName}
              </Typography>
            )}
            {stage.total !== undefined && stage.total > 0 && (
              <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>
                {stage.answered ?? 0}/{stage.total} answered
              </Typography>
            )}
            {stage.completedAt && (
              <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 400 }}>
                Completed: {formatApiLocalDateTime(stage.completedAt)}
              </Typography>
            )}
            {stage.startedAt && !stage.completedAt && (
              <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 400 }}>
                Started: {formatApiLocalDateTime(stage.startedAt)}
              </Typography>
            )}
            {stage.details?.message && (
              <Typography sx={{ fontSize: '12px', color: '#344054', fontWeight: 400 }}>
                {stage.details.message}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const applicantLabel =
    history?.applicantName || (applicantId ? `Applicant #${applicantId}` : '');

  const isEmail = isMedium(history?.communicationMedium, 'email');
  const pendingStep = history?.nextAction.pendingStep;
  // The API returns the welcome step plus completed / in-progress stages only; upcoming stages are not listed.
  const stages = history?.stages ?? [];
  const hasStageProgress = stages.some((stage) => stage.id !== 'welcome');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontSize: '19px', fontWeight: 600, color: '#101828', lineHeight: 1.4 }}>
              Process Summary
            </Typography>
            {(applicantLabel || loading) && (
              <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 400, mt: 0.5 }}>
                {applicantLabel}
                {history?.communicationMedium ? ` · ${history.communicationMedium}` : ''}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close process summary">
            <CloseIcon sx={{ fontSize: 20, color: '#667085' }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider sx={{ borderColor: '#EAECF0' }} />
      <DialogContent
        sx={{
          px: 3,
          py: 2.5,
          maxHeight: 'min(560px, 70vh)',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <Loading message="Loading process summary…" />
        ) : loadError ? (
          <Box
            sx={{
              backgroundColor: '#E9004A1A',
              borderRadius: '8px',
              padding: '16px 20px',
            }}
          >
            <Typography sx={{ fontSize: '13.5px', color: '#E9004A', fontWeight: 500 }}>
              {loadError}
            </Typography>
          </Box>
        ) : history ? (
          <>
            {isEmail && (
              <Box
                sx={{
                  backgroundColor: '#F1F3FF',
                  borderRadius: '8px',
                  padding: '16px 20px',
                  mb: 2,
                  border: '1px solid #EAECF0',
                }}
              >
                <Typography sx={{ fontSize: '13.5px', color: '#667085', fontWeight: 500 }}>
                  Email candidate: stages are allocated only. Record the answers in Additional Info.
                </Typography>
              </Box>
            )}
            {stages.map((stage) => renderStage(stage))}
            {!hasStageProgress && (
              <Typography sx={{ fontSize: '14px', color: '#667085', fontWeight: 400 }}>
                No stage has started yet.
              </Typography>
            )}
            {pendingStep && (
              <Box
                data-testid="pending-step-banner"
                sx={{
                  mt: 1,
                  backgroundColor: '#FFFBF0',
                  borderRadius: '8px',
                  padding: '12px 20px',
                  border: '1px solid #FBB32C66',
                }}
              >
                <Typography sx={{ fontSize: '13.5px', color: '#344054', fontWeight: 500 }}>
                  Stages can start after {PENDING_STEP_LABELS[pendingStep]} is done.
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: '15px', color: '#667085', fontWeight: 400 }}>
            No summary available.
          </Typography>
        )}
      </DialogContent>
      <Divider sx={{ borderColor: '#EAECF0' }} />
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="outlined"
          onClick={fetchHistory}
          disabled={loading}
          width="100px"
          startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
        >
          Refresh
        </Button>
        {onViewDetails && (
          <Button
            variant="contained"
            width="auto"
            sx={{ minWidth: '140px', px: 2 }}
            onClick={onViewDetails}
          >
            View Details
          </Button>
        )}
        <Button variant="outlined" onClick={onClose} width="100px">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessHistoryDialog;
